// ID3v2 (2.2, 2.3, 2.4) and ID3v1 / v1.1 tags.
//
// ID3v2 sits at the start of MP3 and ADTS files (and inside WAV, AIFF and FLAC
// files sometimes). ID3v1 is a fixed 128-byte block at the very end of a file.
//
// Unsynchronisation: ID3v2 can insert a 0x00 after every 0xFF so that no tag
// byte looks like an MPEG sync word. To parse such data we remove the extra
// bytes into a "view" and keep a map from each view index back to the real
// file offset. Every Segment and Field we emit uses real file offsets.
import { ascii, BitFields, field, fmtBytes, hex, matches, plural, synchsafe32, u16be, u24be, u32be, utf16Str, utf8Str } from './bits';
import type { Explanation, Field, Note, Picture, Segment, Tag } from './types';

export interface Id3Result {
  /** One top-level segment for the whole tag (header + frames + padding + footer) with children. */
  segment: Segment;
  tags: Tag[];
  pictures: Picture[];
  notes: Note[];
  /** Byte offset just after the tag. */
  end: number;
}

export const ID3V1_GENRES = [
  'Blues', 'Classic Rock', 'Country', 'Dance', 'Disco', 'Funk', 'Grunge', 'Hip-Hop', 'Jazz', 'Metal', 'New Age', 'Oldies',
  'Other', 'Pop', 'R&B', 'Rap', 'Reggae', 'Rock', 'Techno', 'Industrial', 'Alternative', 'Ska', 'Death Metal', 'Pranks',
  'Soundtrack', 'Euro-Techno', 'Ambient', 'Trip-Hop', 'Vocal', 'Jazz+Funk', 'Fusion', 'Trance', 'Classical', 'Instrumental',
  'Acid', 'House', 'Game', 'Sound Clip', 'Gospel', 'Noise', 'Alternative Rock', 'Bass', 'Soul', 'Punk', 'Space', 'Meditative',
  'Instrumental Pop', 'Instrumental Rock', 'Ethnic', 'Gothic', 'Darkwave', 'Techno-Industrial', 'Electronic', 'Pop-Folk',
  'Eurodance', 'Dream', 'Southern Rock', 'Comedy', 'Cult', 'Gangsta', 'Top 40', 'Christian Rap', 'Pop/Funk', 'Jungle',
  'Native American', 'Cabaret', 'New Wave', 'Psychedelic', 'Rave', 'Showtunes', 'Trailer', 'Lo-Fi', 'Tribal', 'Acid Punk',
  'Acid Jazz', 'Polka', 'Retro', 'Musical', 'Rock & Roll', 'Hard Rock',
  // Winamp extensions (80–191)
  'Folk', 'Folk-Rock', 'National Folk', 'Swing', 'Fast Fusion', 'Bebop', 'Latin', 'Revival', 'Celtic', 'Bluegrass',
  'Avantgarde', 'Gothic Rock', 'Progressive Rock', 'Psychedelic Rock', 'Symphonic Rock', 'Slow Rock', 'Big Band', 'Chorus',
  'Easy Listening', 'Acoustic', 'Humour', 'Speech', 'Chanson', 'Opera', 'Chamber Music', 'Sonata', 'Symphony', 'Booty Bass',
  'Primus', 'Porn Groove', 'Satire', 'Slow Jam', 'Club', 'Tango', 'Samba', 'Folklore', 'Ballad', 'Power Ballad',
  'Rhythmic Soul', 'Freestyle', 'Duet', 'Punk Rock', 'Drum Solo', 'A Cappella', 'Euro-House', 'Dance Hall', 'Goa',
  'Drum & Bass', 'Club-House', 'Hardcore', 'Terror', 'Indie', 'BritPop', 'Afro-Punk', 'Polsk Punk', 'Beat',
  'Christian Gangsta Rap', 'Heavy Metal', 'Black Metal', 'Crossover', 'Contemporary Christian', 'Christian Rock', 'Merengue',
  'Salsa', 'Thrash Metal', 'Anime', 'JPop', 'Synthpop', 'Abstract', 'Art Rock', 'Baroque', 'Bhangra', 'Big Beat', 'Breakbeat',
  'Chillout', 'Downtempo', 'Dub', 'EBM', 'Eclectic', 'Electro', 'Electroclash', 'Emo', 'Experimental', 'Garage', 'Global',
  'IDM', 'Illbient', 'Industro-Goth', 'Jam Band', 'Krautrock', 'Leftfield', 'Lounge', 'Math Rock', 'New Romantic',
  'Nu-Breakz', 'Post-Punk', 'Post-Rock', 'Psytrance', 'Shoegaze', 'Space Rock', 'Trop Rock', 'World Music', 'Neoclassical',
  'Audiobook', 'Audio Theatre', 'Neue Deutsche Welle', 'Podcast', 'Indie Rock', 'G-Funk', 'Dubstep', 'Garage Rock', 'Psybient',
];

const PICTURE_TYPES = [
  'Other', 'File icon', 'Other file icon', 'Front cover', 'Back cover', 'Leaflet page', 'Media', 'Lead artist', 'Artist',
  'Conductor', 'Band', 'Composer', 'Lyricist', 'Recording location', 'During recording', 'During performance',
  'Movie screen capture', 'A bright coloured fish', 'Illustration', 'Band logotype', 'Publisher logotype',
];

// v2.2 used 3-letter frame IDs. We map them to the v2.3/2.4 IDs so one table serves all.
const V22_IDS: Record<string, string> = {
  TT1: 'TIT1', TT2: 'TIT2', TT3: 'TIT3', TP1: 'TPE1', TP2: 'TPE2', TP3: 'TPE3', TP4: 'TPE4', TAL: 'TALB', TYE: 'TYER',
  TRK: 'TRCK', TPA: 'TPOS', TCO: 'TCON', TCM: 'TCOM', TEN: 'TENC', TSS: 'TSSE', TBP: 'TBPM', TCR: 'TCOP', TLE: 'TLEN',
  TXT: 'TEXT', TPB: 'TPUB', TRC: 'TSRC', TLA: 'TLAN', TKE: 'TKEY', TOR: 'TORY', TDA: 'TDAT', TIM: 'TIME', TMT: 'TMED',
  TOA: 'TOPE', TOT: 'TOAL', TOL: 'TOLY', TOF: 'TOFN', TFT: 'TFLT', TSI: 'TSIZ', TRD: 'TRDA', TDY: 'TDLY', TXX: 'TXXX',
  TCP: 'TCMP', TS2: 'TSO2', TSA: 'TSOA', TSC: 'TSOC', TSP: 'TSOP', TST: 'TSOT',
  COM: 'COMM', ULT: 'USLT', PIC: 'APIC', WXX: 'WXXX', WAF: 'WOAF', WAR: 'WOAR', WAS: 'WOAS', WCM: 'WCOM', WCP: 'WCOP',
  WPB: 'WPUB', CNT: 'PCNT', POP: 'POPM', UFI: 'UFID', GEO: 'GEOB', MCI: 'MCDI', SLT: 'SYLT', ETC: 'ETCO', RVA: 'RVAD',
  EQU: 'EQUA', REV: 'RVRB', IPL: 'IPLS', BUF: 'RBUF', CRA: 'AENC', LNK: 'LINK', STC: 'SYTC', MLL: 'MLLT',
};

/** Frame ID → [human name, normalized Tag key (for text frames)]. */
const FRAME_NAMES: Record<string, [string, string?]> = {
  TIT1: ['Grouping', 'Grouping'], TIT2: ['Title', 'Title'], TIT3: ['Subtitle', 'Subtitle'], TPE1: ['Artist', 'Artist'],
  TPE2: ['Album artist', 'Album artist'], TPE3: ['Conductor', 'Conductor'], TPE4: ['Remixed by', 'Remixer'],
  TALB: ['Album', 'Album'], TYER: ['Year', 'Year'], TDRC: ['Recording date', 'Year'], TDAT: ['Date (DDMM)', 'Date'],
  TIME: ['Time (HHMM)', 'Time'], TRDA: ['Recording dates', 'Recording dates'], TDRL: ['Release date', 'Release date'],
  TDOR: ['Original release date', 'Original year'], TORY: ['Original year', 'Original year'], TDTG: ['Tagging time', 'Tagging time'],
  TDEN: ['Encoding time', 'Encoding time'], TRCK: ['Track number', 'Track'], TPOS: ['Disc number', 'Disc'], TCON: ['Genre', 'Genre'],
  TCOM: ['Composer', 'Composer'], TEXT: ['Lyricist', 'Lyricist'], TENC: ['Encoded by', 'Encoded by'], TSSE: ['Encoder settings', 'Encoder'],
  TBPM: ['Beats per minute', 'BPM'], TCOP: ['Copyright', 'Copyright'], TPRO: ['Produced notice', 'Produced'], TLEN: ['Length (ms)', 'Length'],
  TPUB: ['Publisher', 'Publisher'], TSRC: ['ISRC', 'ISRC'], TLAN: ['Language', 'Language'], TKEY: ['Musical key', 'Key'],
  TMED: ['Media type', 'Media'], TMOO: ['Mood', 'Mood'], TOPE: ['Original artist', 'Original artist'], TOAL: ['Original album', 'Original album'],
  TOLY: ['Original lyricist', 'Original lyricist'], TOFN: ['Original filename', 'Original filename'], TFLT: ['File type', 'File type'],
  TSIZ: ['Size', 'Size'], TDLY: ['Playlist delay', 'Playlist delay'], TOWN: ['File owner', 'Owner'], TRSN: ['Radio station', 'Radio station'],
  TRSO: ['Radio station owner', 'Radio station owner'], TSST: ['Set subtitle', 'Set subtitle'], TIPL: ['Involved people', 'Involved people'],
  TMCL: ['Musicians', 'Musicians'], TCMP: ['Compilation (iTunes)', 'Compilation'], TSOA: ['Album sort order', 'Album sort'],
  TSOP: ['Artist sort order', 'Artist sort'], TSOT: ['Title sort order', 'Title sort'], TSO2: ['Album artist sort order', 'Album artist sort'],
  TSOC: ['Composer sort order', 'Composer sort'], IPLS: ['Involved people', 'Involved people'],
  TXXX: ['User text'], COMM: ['Comment'], USLT: ['Lyrics'], SYLT: ['Synced lyrics'], APIC: ['Picture'], WXXX: ['User URL'],
  WCOM: ['Commercial URL'], WCOP: ['Copyright URL'], WOAF: ['Audio file URL'], WOAR: ['Artist URL'], WOAS: ['Source URL'],
  WORS: ['Radio station URL'], WPAY: ['Payment URL'], WPUB: ['Publisher URL'], PRIV: ['Private data'], GEOB: ['Embedded file'],
  UFID: ['Unique file ID'], PCNT: ['Play counter'], POPM: ['Popularimeter (rating)'], CHAP: ['Chapter'], CTOC: ['Table of contents'],
  MCDI: ['CD table of contents'], RVA2: ['Relative volume'], RVAD: ['Relative volume'], ETCO: ['Event timing'], MLLT: ['MPEG lookup table'],
  SYTC: ['Synced tempo'], EQU2: ['Equalisation'], EQUA: ['Equalisation'], RVRB: ['Reverb'], RBUF: ['Buffer size'], AENC: ['Audio encryption'],
  LINK: ['Linked info'], POSS: ['Position sync'], USER: ['Terms of use'], OWNE: ['Ownership'], COMR: ['Commercial'], ENCR: ['Encryption method'],
  GRID: ['Group ID registration'], SIGN: ['Signature'], SEEK: ['Seek'], ASPI: ['Audio seek index'], NCON: ['MusicMatch data'],
};

/** Resolves ID3 genre strings like "(13)", "(13)Pop", "13", "RX", "CR" into names. */
export function resolveGenre(s: string): string {
  const parts: string[] = [];
  const lookup = (n: string) => (n === 'RX' ? 'Remix' : n === 'CR' ? 'Cover' : ID3V1_GENRES[+n] ?? n);
  for (const piece of s.split(/\0| \/ /)) {
    let rest = piece.trim();
    let m: RegExpMatchArray | null;
    let found = false;
    while ((m = rest.match(/^\((\d+|RX|CR)\)/))) {
      parts.push(lookup(m[1]));
      rest = rest.slice(m[0].length);
      found = true;
    }
    if (rest.startsWith('((')) rest = rest.slice(1); // "((" escapes a literal "("
    if (rest && found && parts[parts.length - 1] === rest) continue;
    if (rest) parts.push(/^\d+$/.test(rest) || rest === 'RX' || rest === 'CR' ? lookup(rest) : rest);
  }
  return [...new Set(parts)].join(' / ');
}

// ---------------------------------------------------------------------------
// Views: a byte array plus a map back to real file offsets.

interface View {
  d: Uint8Array;
  /** map[i] = file offset of view byte i. map[d.length] = end of the range. null = identity. */
  map: Int32Array | null;
}
const off = (v: View, i: number) => (v.map ? v.map[Math.min(i, v.map.length - 1)] : i);

/** Removes unsynchronisation (every 0xFF 0x00 becomes 0xFF) from file range [s, e). */
function unsyncView(b: Uint8Array, s: number, e: number): View {
  const d = new Uint8Array(Math.max(0, e - s));
  const map = new Int32Array(d.length + 1);
  let j = 0;
  for (let i = s; i < e; i++) {
    d[j] = b[i];
    map[j++] = i;
    if (b[i] === 0xff && i + 1 < e && b[i + 1] === 0x00) i++;
  }
  map[j] = e;
  return { d: d.subarray(0, j), map: map.subarray(0, j + 1) };
}
/** True when the view range maps to a contiguous, unchanged span of file bytes. */
const contiguous = (v: View, i: number, j: number) => off(v, j) - off(v, i) === j - i;

/** Makes a Field from view indices [i, j). Returns null for zero-length ranges. */
function vf(v: View, name: string, value: string | number, i: number, j: number, note?: string, raw?: number): Field | null {
  const s = off(v, i), e = off(v, j);
  if (e <= s) return null;
  return field(name, value, s, e - s, note, raw);
}

// ---------------------------------------------------------------------------
// Text helpers

const ENCODINGS = ['ISO-8859-1 (Latin-1)', 'UTF-16 with BOM', 'UTF-16BE', 'UTF-8'];
const wide = (enc: number) => enc === 1 || enc === 2;

/** Finds the terminator for a string that starts at i. Returns [textEnd, afterTerminator]. */
function findTerm(d: Uint8Array, i: number, e: number, enc: number): [number, number] {
  if (wide(enc)) {
    for (let p = i; p + 1 < e; p += 2) if (d[p] === 0 && d[p + 1] === 0) return [p, p + 2];
    return [e, e];
  }
  for (let p = i; p < e; p++) if (d[p] === 0) return [p, p + 1];
  return [e, e];
}

function decodeText(d: Uint8Array, i: number, e: number, enc: number): string {
  if (e <= i) return '';
  let s: string;
  if (enc === 1) {
    // UTF-16 with a byte order mark. Some writers repeat the BOM for each value.
    let be = false;
    if (d[i] === 0xfe && d[i + 1] === 0xff) { be = true; i += 2; } else if (d[i] === 0xff && d[i + 1] === 0xfe) i += 2;
    s = utf16Str(d, i, e - i, be);
  } else if (enc === 2) s = utf16Str(d, i, e - i, true);
  else if (enc === 3) s = utf8Str(d, i, e - i);
  else s = ascii(d, i, e - i);
  // Multiple values (v2.4) are separated by NULs; a trailing NUL is just a terminator.
  return s.replace(/﻿/g, '').replace(/\0+$/, '').split('\0').join(' / ');
}

const short = (s: string, n = 80) => (s.length > n ? s.slice(0, n) + '…' : s);

// ---------------------------------------------------------------------------
// Frame body decoding

interface Ctx {
  b: Uint8Array;
  ver: number;
  /** v2.4 tag-level unsync flag: every frame is unsynchronised. */
  tagUnsync24: boolean;
  tags: Tag[];
  pictures: Picture[];
  notes: Note[];
  frameCount: number;
  imageBytes: number;
}

interface Body {
  type: string;
  fields: Field[];
  children?: Segment[];
  summary?: string;
  warning?: string;
}

function push(list: Field[], f: Field | null) {
  if (f) list.push(f);
}

/** Decodes one frame's content. [s, e) are view indices of the frame body (after header extras). */
function decodeBody(ctx: Ctx, id: string, v: View, s: number, e: number, depth: number): Body {
  const d = v.d;
  const fields: Field[] = [];
  const encField = (i: number) => {
    const enc = d[i];
    push(fields, vf(v, 'Text encoding', `${ENCODINGS[enc] ?? 'Unknown'} (${enc})`, i, i + 1, 'How the text bytes are encoded. 0 is Latin-1, 1 and 2 are UTF-16, 3 is UTF-8.', enc));
    return enc > 3 ? 0 : enc;
  };
  const addTag = (key: string, value: string, rawKey: string) => {
    if (value !== '') ctx.tags.push({ key, value, rawKey });
  };

  // Text frames: T*** except TXXX.
  if (id[0] === 'T' && id !== 'TXXX') {
    if (e <= s) return { type: 'id3v2.frame.text', fields, summary: '(empty)' };
    const enc = encField(s);
    let text = decodeText(d, s + 1, e, enc);
    const [name, key] = FRAME_NAMES[id] ?? [id];
    let shown = text;
    if (id === 'TCON') {
      shown = resolveGenre(text);
      if (shown !== text) text = shown;
    }
    push(fields, vf(v, name, shown || '(empty)', s + 1, e, id === 'TCON' ? 'Genre name. Old files use numbers in brackets, like (13) for Pop.' : 'The text value of this frame.'));
    addTag(key ?? id, text, id);
    return { type: 'id3v2.frame.text', fields, summary: short(shown) };
  }
  if (id === 'TXXX' || id === 'WXXX') {
    const enc = encField(s);
    const [de, da] = findTerm(d, s + 1, e, enc);
    const desc = decodeText(d, s + 1, de, enc);
    push(fields, vf(v, 'Description', desc || '(empty)', s + 1, da, 'Names this custom value, since the frame ID alone does not.'));
    if (id === 'TXXX') {
      const val = decodeText(d, da, e, enc);
      push(fields, vf(v, 'Value', val || '(empty)', da, e, 'The custom value.'));
      addTag(TXXX_KEYS[desc.toUpperCase()] ?? desc, val, `TXXX:${desc}`);
      return { type: 'id3v2.frame.TXXX', fields, summary: short(`${desc}: ${val}`) };
    }
    const url = ascii(d, da, e - da).replace(/\0+$/, '');
    push(fields, vf(v, 'URL', url, da, e, 'The link. URLs are always Latin-1 text.'));
    addTag('URL', url, `WXXX:${desc}`);
    return { type: 'id3v2.frame.WXXX', fields, summary: short(url) };
  }
  if (id[0] === 'W') {
    const url = ascii(d, s, e - s).replace(/\0+$/, '');
    push(fields, vf(v, 'URL', url, s, e, 'The link. URLs are always Latin-1 text.'));
    addTag(FRAME_NAMES[id]?.[0] ?? 'URL', url, id);
    return { type: 'id3v2.frame.url', fields, summary: short(url) };
  }
  if (id === 'COMM' || id === 'USLT') {
    const enc = encField(s);
    const lang = ascii(d, s + 1, 3);
    push(fields, vf(v, 'Language', lang.replace(/\0/g, '') || '(none)', s + 1, s + 4, 'A 3-letter ISO 639-2 language code, like "eng".'));
    const [de, da] = findTerm(d, s + 4, e, enc);
    const desc = decodeText(d, s + 4, de, enc);
    push(fields, vf(v, 'Description', desc || '(empty)', s + 4, da, 'A short label. It lets a file hold many comments.'));
    const text = decodeText(d, da, e, enc);
    push(fields, vf(v, id === 'COMM' ? 'Comment' : 'Lyrics', text || '(empty)', da, e, id === 'COMM' ? 'The comment text.' : 'The full lyrics text.'));
    if (id === 'USLT') addTag('Lyrics', text, id);
    else if (!desc) addTag('Comment', text, id);
    else if (!/^iTun/.test(desc)) addTag(`Comment (${desc})`, text, `COMM:${desc}`);
    return { type: `id3v2.frame.${id}`, fields, summary: short(desc ? `${desc}: ${text}` : text) };
  }
  if (id === 'APIC') {
    const enc = encField(s);
    let p = s + 1;
    let mime: string;
    if (ctx.ver === 2) {
      // v2.2 PIC: a 3-letter image format instead of a MIME type.
      const fmt = ascii(d, p, 3);
      mime = fmt.toUpperCase() === 'PNG' ? 'image/png' : fmt.toUpperCase() === 'JPG' ? 'image/jpeg' : `image/${fmt.toLowerCase()}`;
      push(fields, vf(v, 'Image format', fmt, p, p + 3, 'A 3-letter format code (v2.2 only), like JPG or PNG.'));
      p += 3;
    } else {
      const [me, ma] = findTerm(d, p, e, 0);
      mime = ascii(d, p, me - p) || 'image/';
      push(fields, vf(v, 'MIME type', mime, p, ma, 'The image format, like image/jpeg or image/png.'));
      p = ma;
    }
    if (mime === 'image/jpg') mime = 'image/jpeg';
    if (!mime.includes('/')) mime = `image/${mime.toLowerCase()}`;
    const ptype = d[p] ?? 0;
    push(fields, vf(v, 'Picture type', `${PICTURE_TYPES[ptype] ?? 'Unknown'} (${ptype})`, p, p + 1, 'What the picture shows. 3 means front cover.', ptype));
    p++;
    const [de, da] = findTerm(d, p, e, enc);
    const desc = decodeText(d, p, de, enc);
    push(fields, vf(v, 'Description', desc || '(empty)', p, da, 'Free text about the picture.'));
    const dataLen = e - da;
    push(fields, vf(v, 'Image data', fmtBytes(dataLen), da, e, 'The raw image file, byte for byte.', dataLen));
    const is = off(v, da), ie = off(v, e);
    const clean = contiguous(v, da, e);
    const infoEnd = off(v, da);
    const children: Segment[] = [];
    if (infoEnd > off(v, s)) children.push({ kind: 'metadata', type: 'id3v2.picture.info', name: 'Picture info', start: off(v, s), end: infoEnd, fields: fields.slice(0, -1) });
    if (ie > is) {
      const img: Segment = { kind: 'image', type: 'id3v2.image', name: `${mime} data`, start: is, end: ie, summary: fmtBytes(ie - is) };
      if (clean) {
        img.image = { mime, start: is, end: ie };
        ctx.pictures.push({ mime, start: is, end: ie, description: desc, pictureType: PICTURE_TYPES[ptype] });
      } else img.warning = 'These image bytes are unsynchronised (extra 0x00 bytes were inserted), so they cannot be shown as-is.';
      children.push(img);
    }
    ctx.imageBytes += dataLen;
    return { type: 'id3v2.frame.APIC', fields, children, summary: `${PICTURE_TYPES[ptype] ?? 'Picture'} · ${mime} · ${fmtBytes(dataLen)}` };
  }
  if (id === 'PRIV' || id === 'UFID') {
    const [oe, oa] = findTerm(d, s, e, 0);
    const owner = ascii(d, s, oe - s);
    push(fields, vf(v, 'Owner', owner || '(empty)', s, oa, 'Who wrote this data, usually a URL or email address.'));
    let summary = owner;
    if (id === 'UFID') {
      const ident = ascii(d, oa, e - oa);
      push(fields, vf(v, 'Identifier', /^[\x20-\x7e]*$/.test(ident) ? ident : `${e - oa} bytes`, oa, e, 'An ID that is unique in the owner\'s database, like a MusicBrainz ID.'));
      summary = `${owner}: ${ident}`;
    } else push(fields, vf(v, 'Data', fmtBytes(e - oa), oa, e, 'Binary data that only the owner\'s software understands.'));
    return { type: `id3v2.frame.${id}`, fields, summary: short(summary) };
  }
  if (id === 'GEOB') {
    const enc = encField(s);
    const [me, ma] = findTerm(d, s + 1, e, 0);
    const mime = ascii(d, s + 1, me - s - 1);
    push(fields, vf(v, 'MIME type', mime || '(empty)', s + 1, ma, 'The type of the embedded file.'));
    const [fe, fa] = findTerm(d, ma, e, enc);
    const fname = decodeText(d, ma, fe, enc);
    push(fields, vf(v, 'Filename', fname || '(empty)', ma, fa, 'The name of the embedded file.'));
    const [de, da] = findTerm(d, fa, e, enc);
    const desc = decodeText(d, fa, de, enc);
    push(fields, vf(v, 'Description', desc || '(empty)', fa, da, 'Free text about the embedded file.'));
    push(fields, vf(v, 'Data', fmtBytes(e - da), da, e, 'The embedded file, byte for byte.'));
    return { type: 'id3v2.frame.GEOB', fields, summary: short(`${fname || desc || mime} · ${fmtBytes(e - da)}`) };
  }
  if (id === 'PCNT') {
    let n = 0;
    for (let p = s; p < e; p++) n = n * 256 + d[p];
    push(fields, vf(v, 'Play count', n, s, e, 'How many times the file was played.'));
    return { type: 'id3v2.frame.PCNT', fields, summary: plural(n, 'play') };
  }
  if (id === 'POPM') {
    const [ee, ea] = findTerm(d, s, e, 0);
    const email = ascii(d, s, ee - s);
    push(fields, vf(v, 'Email', email || '(empty)', s, ea, 'Identifies the user or program that gave the rating.'));
    const rating = d[ea] ?? 0;
    push(fields, vf(v, 'Rating', `${rating} / 255`, ea, ea + 1, '1 is worst and 255 is best. 0 means not rated.', rating));
    let n = 0;
    for (let p = ea + 1; p < e; p++) n = n * 256 + d[p];
    push(fields, vf(v, 'Play count', n, ea + 1, e, 'How many times the file was played.'));
    addTag('Rating', `${rating}/255`, 'POPM');
    return { type: 'id3v2.frame.POPM', fields, summary: `Rating ${rating}/255` };
  }
  if (id === 'CHAP' || id === 'CTOC') {
    const [ie2, ia] = findTerm(d, s, e, 0);
    const elem = ascii(d, s, ie2 - s);
    push(fields, vf(v, 'Element ID', elem || '(empty)', s, ia, 'A name that other frames use to refer to this one.'));
    let p = ia;
    let summary = elem;
    if (id === 'CHAP') {
      const t0 = u32be(d, p), t1 = u32be(d, p + 4);
      push(fields, vf(v, 'Start time', `${(t0 / 1000).toFixed(3)} s`, p, p + 4, 'Where the chapter starts, in milliseconds.', t0));
      push(fields, vf(v, 'End time', `${(t1 / 1000).toFixed(3)} s`, p + 4, p + 8, 'Where the chapter ends, in milliseconds.', t1));
      const o0 = u32be(d, p + 8), o1 = u32be(d, p + 12);
      push(fields, vf(v, 'Start offset', o0 === 0xffffffff ? 'not used' : o0, p + 8, p + 12, 'Byte offset of the start; 0xFFFFFFFF means "use the time".', o0));
      push(fields, vf(v, 'End offset', o1 === 0xffffffff ? 'not used' : o1, p + 12, p + 16, 'Byte offset of the end; 0xFFFFFFFF means "use the time".', o1));
      p += 16;
      summary = `${elem} · ${(t0 / 1000).toFixed(1)}–${(t1 / 1000).toFixed(1)} s`;
    } else {
      const flags = d[p] ?? 0, count = d[p + 1] ?? 0;
      push(fields, vf(v, 'Flags', `${flags & 2 ? 'top level' : 'nested'}${flags & 1 ? ', ordered' : ''}`, p, p + 1, 'Bit 1 marks the top-level table; bit 0 means the entries are in order.', flags));
      push(fields, vf(v, 'Entry count', count, p + 1, p + 2, 'How many child elements this table lists.'));
      p += 2;
      const ids: string[] = [];
      for (let k = 0; k < count && p < e; k++) {
        const [ce, ca] = findTerm(d, p, e, 0);
        ids.push(ascii(d, p, ce - p));
        push(fields, vf(v, `Entry ${k + 1}`, ids[k], p, ca, 'The element ID of a chapter or sub-table.'));
        p = ca;
      }
      summary = `${elem} · ${plural(count, 'entry')}`.replace('entrys', 'entries');
    }
    // Embedded sub-frames (for example a TIT2 with the chapter title).
    const children: Segment[] = [];
    if (p < e && depth < 3) {
      const sub: Ctx = { ...ctx, tags: [], pictures: ctx.pictures };
      parseFrames(sub, v, p, e, children, depth + 1, false);
      const title = sub.tags.find((t) => t.key === 'Title');
      if (title) summary += ` · ${title.value}`;
    }
    return { type: `id3v2.frame.${id}`, fields, children, summary: short(summary) };
  }
  push(fields, vf(v, 'Data', fmtBytes(e - s), s, e, 'The content of this frame. This tool does not decode this frame type.'));
  return { type: 'id3v2.frame', fields, summary: fmtBytes(e - s) };
}

const TXXX_KEYS: Record<string, string> = {
  'REPLAYGAIN_TRACK_GAIN': 'ReplayGain (track)', 'REPLAYGAIN_ALBUM_GAIN': 'ReplayGain (album)',
  'MUSICBRAINZ ALBUM ID': 'MusicBrainz album ID', 'MUSICBRAINZ ARTIST ID': 'MusicBrainz artist ID',
  'ALBUMARTIST': 'Album artist', 'ALBUM ARTIST': 'Album artist',
};

const isIdChar = (c: number) => (c >= 0x41 && c <= 0x5a) || (c >= 0x30 && c <= 0x39);

/** True when there is a plausible frame header (or padding / end) at view index p. */
function plausibleNext(v: View, p: number, e: number, idLen: number): boolean {
  if (p === e) return true;
  if (p > e) return false;
  if (v.d[p] === 0) return true;
  for (let k = 0; k < idLen; k++) if (!isIdChar(v.d[p + k])) return false;
  return true;
}

/**
 * Parses frames in view range [s, e) and appends segments to `out`. Handles
 * padding and garbage at the end. `top` marks the tag's own frame list (vs.
 * frames nested in CHAP/CTOC).
 */
function parseFrames(ctx: Ctx, v: View, s: number, e: number, out: Segment[], depth: number, top: boolean): void {
  const { ver } = ctx;
  const d = v.d;
  const idLen = ver === 2 ? 3 : 4;
  const hLen = ver === 2 ? 6 : 10;
  let p = s;
  while (p < e) {
    if (d[p] === 0 || p + hLen > e) {
      // Padding: zero bytes that reserve room so tag edits need not rewrite the file.
      let nonZero = false;
      for (let k = p; k < e; k++) if (d[k] !== 0) { nonZero = true; break; }
      const seg: Segment = {
        kind: nonZero ? 'unknown' : 'padding', type: nonZero ? 'id3v2.unknown' : 'id3v2.padding',
        name: nonZero ? 'Unparsed bytes' : 'Padding', start: off(v, p), end: off(v, e), summary: fmtBytes(off(v, e) - off(v, p)),
      };
      if (nonZero) seg.warning = 'Expected padding (all zeros) here, but found other bytes.';
      if (seg.end > seg.start) out.push(seg);
      if (top && !nonZero && seg.end > seg.start) ctx.notes.push({ level: 'info', text: `The ID3 tag ends with ${fmtBytes(seg.end - seg.start)} of padding. Tag editors can grow the tag into this space without rewriting the whole file.` });
      return;
    }
    let ok = true;
    for (let k = 0; k < idLen; k++) if (!isIdChar(d[p + k])) ok = false;
    if (!ok) {
      out.push({
        kind: 'unknown', type: 'id3v2.unknown', name: 'Unparsed bytes', start: off(v, p), end: off(v, e),
        warning: `Expected a frame ID (4 capital letters or digits) at byte ${off(v, p)}, but found ${hex(d[p])}. The rest of the tag is not parsed.`,
      });
      return;
    }
    const rawId = ascii(d, p, idLen);
    const id = ver === 2 ? V22_IDS[rawId] ?? rawId : rawId;
    let size: number;
    let sizeNote = 'Size of the frame content, not counting this 10-byte header.';
    if (ver === 2) {
      size = u24be(d, p + 3);
      sizeNote = 'Size of the frame content, not counting this 6-byte header.';
    } else if (ver === 3) size = u32be(d, p + 4);
    else {
      // v2.4 uses synchsafe sizes, but some old writers (early iTunes) used plain
      // integers. Pick the one that lands on a sensible next frame.
      size = synchsafe32(d, p + 4);
      const plain = u32be(d, p + 4);
      const bad = (d[p + 4] | d[p + 5] | d[p + 6] | d[p + 7]) & 0x80;
      if (plain !== size && (bad || (!plausibleNext(v, p + 10 + size, e, 4) && plausibleNext(v, p + 10 + plain, e, 4)))) {
        size = plain;
        sizeNote = 'Size of the frame content. This writer used a plain 32-bit number here, not the synchsafe form v2.4 asks for.';
      } else sizeNote = 'Size of the frame content, as a synchsafe integer (7 bits per byte).';
    }
    let fe = p + hLen + size;
    let warning: string | undefined;
    if (fe > e) {
      warning = `This frame claims ${size.toLocaleString('en-US')} bytes, which runs past the end of the tag.`;
      fe = e;
    }
    const hdrFields: Field[] = [];
    push(hdrFields, vf(v, 'Frame ID', rawId, p, p + idLen, `Says what the frame holds. "${rawId}" means ${FRAME_NAMES[id]?.[0] ?? 'an unknown or custom frame'}.`));
    push(hdrFields, vf(v, 'Size', size, p + idLen, p + idLen + (ver === 2 ? 3 : 4), sizeNote));
    let bs = p + hLen;
    let compressed = false, encrypted = false, unsync = false;
    if (ver >= 3) {
      const f1 = d[p + 8], f2 = d[p + 9];
      const names: string[] = [];
      if (ver === 3) {
        if (f1 & 0x80) names.push('discard on tag change');
        if (f1 & 0x40) names.push('discard on file change');
        if (f1 & 0x20) names.push('read-only');
        compressed = !!(f2 & 0x80); encrypted = !!(f2 & 0x40);
        const grouped = !!(f2 & 0x20);
        if (compressed) names.push('compressed');
        if (encrypted) names.push('encrypted');
        if (grouped) names.push('grouped');
        push(hdrFields, vf(v, 'Flags', names.join(', ') || 'none', p + 8, p + 10, 'Status and format flags: how to treat this frame when the tag or file changes, and how it is stored.', (f1 << 8) | f2));
        if (compressed) { push(hdrFields, vf(v, 'Decompressed size', u32be(d, bs), bs, bs + 4, 'Size of the frame content after zlib decompression.')); bs += 4; }
        if (encrypted) { push(hdrFields, vf(v, 'Encryption method', d[bs], bs, bs + 1, 'Points to an ENCR frame that describes the method.')); bs += 1; }
        if (grouped) { push(hdrFields, vf(v, 'Group ID', d[bs], bs, bs + 1, 'Frames with the same group ID belong together.')); bs += 1; }
      } else {
        if (f1 & 0x40) names.push('discard on tag change');
        if (f1 & 0x20) names.push('discard on file change');
        if (f1 & 0x10) names.push('read-only');
        const grouped = !!(f2 & 0x40);
        compressed = !!(f2 & 0x08); encrypted = !!(f2 & 0x04); unsync = !!(f2 & 0x02) || ctx.tagUnsync24;
        const dli = !!(f2 & 0x01);
        if (grouped) names.push('grouped');
        if (compressed) names.push('compressed');
        if (encrypted) names.push('encrypted');
        if (f2 & 0x02) names.push('unsynchronised');
        if (dli) names.push('data length indicator');
        push(hdrFields, vf(v, 'Flags', names.join(', ') || 'none', p + 8, p + 10, 'Status and format flags: how to treat this frame when the tag or file changes, and how it is stored.', (f1 << 8) | f2));
        if (grouped) { push(hdrFields, vf(v, 'Group ID', d[bs], bs, bs + 1, 'Frames with the same group ID belong together.')); bs += 1; }
        if (encrypted) { push(hdrFields, vf(v, 'Encryption method', d[bs], bs, bs + 1, 'Points to an ENCR frame that describes the method.')); bs += 1; }
        if (dli) { push(hdrFields, vf(v, 'Data length', synchsafe32(d, bs), bs, bs + 4, 'Size of the content after removing compression and unsynchronisation.')); bs += 4; }
      }
    }
    bs = Math.min(bs, fe);
    ctx.frameCount++;
    let body: Body;
    if (compressed || encrypted) {
      const f: Field[] = [];
      push(f, vf(v, 'Data', fmtBytes(fe - bs), bs, fe, compressed ? 'zlib-compressed content.' : 'Encrypted content.'));
      body = { type: 'id3v2.frame', fields: f, summary: compressed ? 'Compressed' : 'Encrypted', warning: `This frame is ${compressed ? 'compressed' : 'encrypted'}, so this tool does not decode it.` };
    } else if (unsync && ver === 4) {
      // v2.4 frame-level unsynchronisation: decode the body into its own view.
      const bv = unsyncView(ctx.b, off(v, bs), off(v, fe));
      body = decodeBody(ctx, id, bv, 0, bv.d.length, depth);
    } else {
      body = decodeBody(ctx, id, v, bs, fe, depth);
    }
    const start = off(v, p), end = off(v, fe);
    const children: Segment[] = [{ kind: 'header', type: 'id3v2.frame.header', name: 'Frame header', start, end: off(v, bs), fields: hdrFields }];
    if (body.children?.length) children.push(...body.children);
    const friendly = FRAME_NAMES[id]?.[0];
    const seg: Segment = {
      kind: 'metadata', type: body.type, name: friendly ? `${rawId} · ${friendly}` : rawId, start, end,
      summary: body.summary, fields: [...hdrFields, ...body.fields], children: children.filter((c) => c.end > c.start),
    };
    const w = [warning, body.warning].filter(Boolean).join(' ');
    if (w) seg.warning = w;
    out.push(seg);
    p = fe;
  }
}

// ---------------------------------------------------------------------------

/** Parses an ID3v2 tag at `start`. Returns null when there is no valid "ID3" header there. */
export function parseId3v2(b: Uint8Array, start: number): Id3Result | null {
  if (!matches(b, start, 'ID3') || start + 10 > b.length) return null;
  const ver = b[start + 3], rev = b[start + 4], flags = b[start + 5];
  if (ver < 2 || ver > 4 || ver === 0xff || rev === 0xff) return null;
  if ((b[start + 6] | b[start + 7] | b[start + 8] | b[start + 9]) & 0x80) return null;
  const size = synchsafe32(b, start + 6);
  const hasFooter = ver === 4 && !!(flags & 0x10);
  const tagUnsync = !!(flags & 0x80);
  const hasExt = ver >= 3 && !!(flags & 0x40);
  const notes: Note[] = [];
  const tags: Tag[] = [];
  const pictures: Picture[] = [];
  let bodyEnd = start + 10 + size;
  let end = bodyEnd + (hasFooter ? 10 : 0);
  let warning: string | undefined;
  if (end > b.length) {
    warning = `The header says the tag is ${fmtBytes(end - start)}, but the file ends first.`;
    end = b.length;
    bodyEnd = Math.min(bodyEnd, b.length);
  }

  // Header, with its flags as bit fields.
  const bf = new BitFields(b, start);
  const hf: Field[] = [field('File identifier', 'ID3', start, 3, 'The three letters "ID3" mark the start of the tag.')];
  hf.push(field('Version', `2.${ver}.${rev}`, start + 3, 2, 'Major version and revision. Version 2.3 is the most common; 2.4 is the newest.', ver));
  bf.skip(40);
  bf.read('Unsynchronisation', 1, (x) => (x ? 'yes' : 'no'), 'If set, a 0x00 byte follows each 0xFF so old players never mistake tag bytes for audio.');
  if (ver === 2) bf.read('Compression', 1, (x) => (x ? 'yes' : 'no'), 'v2.2 defined compression but no method, so readers skip such tags.');
  else bf.read('Extended header', 1, (x) => (x ? 'yes' : 'no'), 'If set, an extended header with extra tag info follows.');
  bf.read('Experimental', 1, (x) => (x ? 'yes' : 'no'), 'Marks a tag that is still being tested.');
  if (ver === 4) bf.read('Footer present', 1, (x) => (x ? 'yes' : 'no'), 'If set, a copy of the header ("3DI") follows the tag, so it can be found from the end.');
  else bf.read('Reserved', 1, undefined, 'Always 0.');
  bf.read('Reserved', 4, undefined, 'Always 0.');
  hf.push(...bf.fields);
  hf.push(field('Tag size', size, start + 6, 4, 'Size of everything after this header (not the footer). Synchsafe: only 7 bits per byte are used, so it never contains 0xFF.'));
  const children: Segment[] = [{ kind: 'header', type: 'id3v2.header', name: 'Tag header', start, end: start + 10, fields: hf, summary: `ID3v2.${ver}.${rev}` }];

  // v2.2/2.3: unsynchronisation applies to the whole body. v2.4: only to frame data.
  const view: View = tagUnsync && ver < 4 ? unsyncView(b, start + 10, bodyEnd) : { d: b, map: null };
  let p = view.map ? 0 : start + 10;
  const e = view.map ? view.d.length : bodyEnd;
  if (tagUnsync) notes.push({ level: 'info', text: `The ID3v2.${ver} tag is unsynchronised: a 0x00 follows every 0xFF byte, so no tag byte can look like an MPEG sync word to an old player.` });

  if (hasExt && p + 4 <= e) {
    const d = view.d;
    const ef: Field[] = [];
    let extEnd: number;
    if (ver === 3) {
      const extSize = u32be(d, p);
      extEnd = Math.min(e, p + 4 + extSize);
      push(ef, vf(view, 'Extended header size', extSize, p, p + 4, 'Size of the rest of the extended header: 6, or 10 with a CRC.'));
      const eflags = u16be(d, p + 4);
      push(ef, vf(view, 'Flags', eflags & 0x8000 ? 'CRC present' : 'none', p + 4, p + 6, 'Bit 15 says a CRC-32 of the frames follows.', eflags));
      push(ef, vf(view, 'Padding size', u32be(d, p + 6), p + 6, p + 10, 'How many bytes of padding the tag has at its end.'));
      if (eflags & 0x8000) push(ef, vf(view, 'CRC-32', hex(u32be(d, p + 10), 8), p + 10, p + 14, 'A checksum of the frame data, to detect damage.'));
    } else {
      const extSize = synchsafe32(d, p);
      extEnd = Math.min(e, p + Math.max(6, extSize));
      push(ef, vf(view, 'Extended header size', extSize, p, p + 4, 'Size of the whole extended header, as a synchsafe integer.'));
      push(ef, vf(view, 'Flag bytes', d[p + 4], p + 4, p + 5, 'Number of flag bytes that follow. Always 1.'));
      const f = d[p + 5];
      const names = [f & 0x40 ? 'update' : '', f & 0x20 ? 'CRC present' : '', f & 0x10 ? 'restrictions' : ''].filter(Boolean);
      push(ef, vf(view, 'Flags', names.join(', ') || 'none', p + 5, p + 6, 'Update means this tag adds to an earlier one. The others add a CRC or size limits.', f));
      let q = p + 6;
      if (f & 0x40 && q < extEnd) { push(ef, vf(view, 'Update data length', d[q], q, q + 1, 'Always 0: the update flag has no data.')); q += 1 + d[q]; }
      if (f & 0x20 && q < extEnd) { const n = d[q]; push(ef, vf(view, 'CRC-32', hex(synchsafe32(d, q + 1) * 16 + (d[q + 5] & 0x0f), 8), q, q + 1 + n, 'A checksum of the frame data, stored as 5 synchsafe bytes.')); q += 1 + n; }
      if (f & 0x10 && q < extEnd) { const r = d[q + 1]; push(ef, vf(view, 'Restrictions', hex(r), q, q + 2, 'Limits on tag size, text encoding, text length and image size.', r)); }
    }
    children.push({ kind: 'header', type: 'id3v2.extheader', name: 'Extended header', start: off(view, p), end: off(view, extEnd), fields: ef });
    p = extEnd;
  }

  const ctx: Ctx = { b, ver, tagUnsync24: tagUnsync && ver === 4, tags, pictures, notes, frameCount: 0, imageBytes: 0 };
  parseFrames(ctx, view, p, e, children, 0, true);

  if (hasFooter && bodyEnd + 10 <= b.length) {
    const fs = bodyEnd;
    const ff: Field[] = [field('Footer identifier', ascii(b, fs, 3), fs, 3, '"3DI" is "ID3" backwards: it marks the end of a tag.'), field('Version', `2.${b[fs + 3]}.${b[fs + 4]}`, fs + 3, 2), field('Flags', hex(b[fs + 5]), fs + 5, 1, 'A copy of the header flags.'), field('Tag size', synchsafe32(b, fs + 6), fs + 6, 4, 'A copy of the header tag size.')];
    children.push({ kind: 'header', type: 'id3v2.footer', name: 'Tag footer', start: fs, end: fs + 10, fields: ff, warning: matches(b, fs, '3DI') ? undefined : 'The footer should start with "3DI".' });
  }

  if (ctx.imageBytes > 0 && end - start > 0 && ctx.imageBytes / (end - start) > 0.5)
    notes.push({ level: 'info', text: `Cover art makes up ${Math.round((ctx.imageBytes / (end - start)) * 100)}% of the ID3 tag (${fmtBytes(ctx.imageBytes)}).` });

  const segment: Segment = {
    kind: 'metadata', type: 'id3v2', name: `ID3v2.${ver} tag`, start, end, fields: hf,
    summary: `${plural(ctx.frameCount, 'frame')} · ${fmtBytes(end - start)}`,
    children: children.filter((c) => c.end <= end),
  };
  if (warning) segment.warning = warning;
  return { segment, tags, pictures, notes, end };
}

/** Parses an ID3v1 tag (128 bytes starting with "TAG") at `start`, or returns null. */
export function parseId3v1(b: Uint8Array, start: number): Omit<Id3Result, 'notes' | 'pictures'> | null {
  if (start < 0 || !matches(b, start, 'TAG') || start + 128 > b.length) return null;
  const str = (o: number, n: number) => ascii(b, start + o, n).replace(/\0.*$/s, '').trimEnd();
  const tags: Tag[] = [];
  const fields: Field[] = [field('Identifier', 'TAG', start, 3, 'The letters "TAG" mark an ID3v1 tag, always the last 128 bytes of the file.')];
  const text = (name: string, key: string, o: number, n: number, note: string) => {
    const s = str(o, n);
    fields.push(field(name, s || '(empty)', start + o, n, note));
    if (s) tags.push({ key, value: s, rawKey: `ID3v1 ${name}` });
    return s;
  };
  const title = text('Title', 'Title', 3, 30, 'Fixed 30 bytes, padded with zeros. Longer titles are cut off.');
  const artist = text('Artist', 'Artist', 33, 30, 'Fixed 30 bytes.');
  text('Album', 'Album', 63, 30, 'Fixed 30 bytes.');
  text('Year', 'Year', 93, 4, 'Four ASCII digits.');
  // ID3v1.1: if byte 125 is zero and byte 126 is not, byte 126 is the track number.
  const v11 = b[start + 125] === 0 && b[start + 126] !== 0;
  if (v11) {
    text('Comment', 'Comment', 97, 28, 'ID3v1.1 shortens the comment to 28 bytes to make room for a track number.');
    fields.push(field('Zero byte', 0, start + 125, 1, 'A zero here tells readers that the next byte is a track number (v1.1).'));
    fields.push(field('Track', b[start + 126], start + 126, 1, 'Track number on the album (v1.1).'));
    tags.push({ key: 'Track', value: String(b[start + 126]), rawKey: 'ID3v1 Track' });
  } else text('Comment', 'Comment', 97, 30, 'Fixed 30 bytes.');
  const g = b[start + 127];
  const genre = ID3V1_GENRES[g];
  fields.push(field('Genre', genre ? `${genre} (${g})` : g === 255 ? 'none (255)' : `Unknown (${g})`, start + 127, 1, 'An index into a fixed list of genres; 255 means none.', g));
  if (genre) tags.push({ key: 'Genre', value: genre, rawKey: 'ID3v1 Genre' });
  const summary = [artist, title].filter(Boolean).join(' – ') || '(empty)';
  return {
    segment: { kind: 'metadata', type: 'id3v1', name: v11 ? 'ID3v1.1 tag' : 'ID3v1 tag', start, end: start + 128, summary, fields },
    tags, end: start + 128,
  };
}

export const id3Explanations: Record<string, Explanation> = {
  id3v2: {
    title: 'ID3v2 tag',
    body: 'A block of metadata at the start of the file: title, artist, cover art and more. MPEG audio has no place for tags, so ID3 puts them before the first audio frame. Players skip it using the size in its header.',
  },
  'id3v2.header': {
    title: 'ID3v2 header',
    body: 'Ten bytes that start the tag: the letters `ID3`, the version, some flags, and the tag size. The size is **synchsafe**: each byte uses only 7 bits, so the header never contains a byte pattern that looks like MPEG audio.',
  },
  'id3v2.extheader': {
    title: 'Extended header',
    body: 'Optional extra information about the whole tag, like a checksum or limits on its size. Few programs write it.',
  },
  'id3v2.footer': {
    title: 'ID3v2 footer',
    body: 'A copy of the header, starting with `3DI`. It lets a program find the tag by reading backwards from the end, for example when the tag is appended to a file.',
  },
  'id3v2.padding': {
    title: 'Padding',
    body: 'Zero bytes kept free on purpose. When you edit a tag, the program can use this space and does not have to rewrite the whole audio file.',
  },
  'id3v2.unknown': {
    title: 'Unparsed tag bytes',
    body: 'Bytes inside the tag that do not look like a valid frame or padding. A buggy tag writer usually causes this.',
  },
  'id3v2.frame.header': {
    title: 'Frame header',
    body: 'Every ID3 frame starts with an ID (like `TIT2`), a size, and flags (10 bytes; 6 in v2.2). The size lets a reader skip frames it does not understand.',
  },
  'id3v2.frame': {
    title: 'ID3v2 frame',
    body: 'One item in the tag. The 4-letter ID says what kind of data it holds. This tool shows the raw content for frame types it does not decode.',
  },
  'id3v2.frame.text': {
    title: 'Text frame',
    body: 'A text value such as title (`TIT2`), artist (`TPE1`) or album (`TALB`). The first byte picks the text encoding: Latin-1, UTF-16 or UTF-8. Version 2.4 can hold several values separated by zero bytes.',
  },
  'id3v2.frame.TXXX': {
    title: 'User-defined text',
    body: 'A custom text value with its own name. Programs use it for data that has no standard frame, like ReplayGain values or MusicBrainz IDs.',
  },
  'id3v2.frame.COMM': {
    title: 'Comment',
    body: 'Free text with a language code and a short description. The description lets one file hold several comments; iTunes uses this for its own data.',
  },
  'id3v2.frame.USLT': {
    title: 'Lyrics',
    body: 'The song words as plain text, with a language code. Unlike `SYLT`, it has no timing, so players cannot highlight the current line.',
  },
  'id3v2.frame.APIC': {
    title: 'Attached picture',
    body: 'Cover art or another image. A small header gives the image type (like `image/jpeg`), what it shows, and a description. The image file follows as-is, so it is often the largest part of the tag.',
  },
  'id3v2.picture.info': {
    title: 'Picture info',
    body: 'The fields that come before the image bytes: text encoding, MIME type, picture type and description.',
  },
  'id3v2.image': {
    title: 'Image data',
    body: 'The raw bytes of the embedded picture, usually a JPEG or PNG file. The audio player ignores these bytes. Large cover art can take a big share of a small file.',
  },
  'id3v2.frame.url': {
    title: 'Link',
    body: 'A web address about the file, such as the artist\'s site or where to buy the track. The frame ID says which kind of link it is.',
  },
  'id3v2.frame.WXXX': {
    title: 'User-defined link',
    body: 'A web address with a free-text description, for links that have no standard frame.',
  },
  'id3v2.frame.PRIV': {
    title: 'Private data',
    body: 'Binary data for one program, marked with an owner name. Windows Media Player and others store their own IDs here. Other programs just skip it.',
  },
  'id3v2.frame.GEOB': {
    title: 'Embedded file',
    body: 'Any file stored inside the tag, with a MIME type, filename and description. DJ software uses it to save cue points and beat grids.',
  },
  'id3v2.frame.UFID': {
    title: 'Unique file identifier',
    body: 'An ID that links this file to a record in a database, such as MusicBrainz. The owner field names the database.',
  },
  'id3v2.frame.PCNT': {
    title: 'Play counter',
    body: 'How many times the file was played. Few players update it, because that means writing to the file.',
  },
  'id3v2.frame.POPM': {
    title: 'Popularimeter',
    body: 'A rating from 1 to 255 and a play count, tied to an email address or program name. Windows and many music apps store star ratings here.',
  },
  'id3v2.frame.CHAP': {
    title: 'Chapter',
    body: 'One chapter of a podcast or audiobook, with start and end times. Frames inside it, like a title, describe the chapter.',
  },
  'id3v2.frame.CTOC': {
    title: 'Table of contents',
    body: 'Lists the chapters (`CHAP` frames) in order, so a player can show a chapter menu.',
  },
  id3v1: {
    title: 'ID3v1 tag',
    body: 'The original MP3 tag from 1996: exactly 128 bytes at the very end of the file, starting with `TAG`. Each field has a fixed size, so long titles get cut off. Genre is a number from a fixed list.',
  },
};
